import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Button,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import { getChoiceNamesBySource, SOURCE_NAMES }
  from '@lba-dev/package.local-globals/calendarSources';
import { LEAD_PLANNING_HOURS }
  from '@lba-dev/package.local-globals/leadPlanning';

import { setDialogContentProp, setDialog } from '../../../actions/dialog';
import CalendrierSelector from './CalendrierSelector';
import TimeSelector from '../../CustomInputs/CustomTimeSelector';
import styles from './styles';
import * as utils from './utils';

const mapStateToProps = ({ dialog }) => dialog.contentProps || {};

const mapDispatchToProps = (dispatch) => ({
  setDialogContentProp: (key) => (value) =>
    dispatch(setDialogContentProp(key)(value)),
  dispatch,
});

function GestionLeadsConfigDialog({
  block,
  slotInfo,
  existingBlocks = [],
  originalBlocks = [],
  commerciaux = [],
  onRefreshData,
  classes,
  setDialogContentProp,
  dispatch,
  source = SOURCE_NAMES.LEROY_MERLIN,
}) {
  const calendriers = useMemo(
    () => getChoiceNamesBySource(source),
    [source]
  );
  const [calendrier, setCalendrier] = useState(calendriers[0]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [distributions, setDistributions] = useState([]);

  useEffect(() => {
    if (block) {
      // Mode édition
      const start = dayjs(block.start);
      const end = dayjs(block.end);
      setStartTime(start.format('HH:mm'));
      setEndTime(end.format('HH:mm'));
      setCalendrier(block.calendrier || calendriers[0]);
      setDistributions(block.distributions || []);
    } else if (slotInfo) {
      // Mode création depuis un slot
      const start = dayjs(slotInfo.start);
      const end = dayjs(slotInfo.end);
      setStartTime(start.format('HH:mm'));
      setEndTime(end.format('HH:mm'));
      setDistributions([]);
    }
  }, [block, slotInfo, calendriers]);

  const handleAddDistribution = () => {
    setDistributions([
      ...distributions,
      { userId: '', percentage: '' },
    ]);
  };

  const handleRemoveDistribution = (index) => {
    setDistributions(distributions.filter((_, i) => i !== index));
  };

  const handleDistributionChange = (index, field, value) => {
    const updatedDistributions = [...distributions];
    if (field === 'percentage') {
      // Garder la valeur vide si l'input est vide, sinon parser en nombre
      updatedDistributions[index][field] = value === ''
        ? ''
        : parseFloat(value) || 0;
    } else {
      updatedDistributions[index][field] = value;
    }
    setDistributions(updatedDistributions);
  };

  const isUserOnlyInSavedSlot = (userId) => {
    if (!block || !originalBlocks || originalBlocks.length === 0) {
      return true;
    }

    const slot = {
      day: dayjs(block.start).day(),
      startTime: dayjs(block.start).format('HH:mm'),
      endTime: dayjs(block.end).format('HH:mm'),
      calendrier: block.calendrier,
    };

    const matchingSlot = originalBlocks.find(
      b =>
        b.day === slot.day &&
        b.startTime === slot.startTime &&
        b.endTime === slot.endTime &&
        b.calendrier === slot.calendrier
    );

    return !matchingSlot ||
      matchingSlot.distributions.length <= 1 ||
      (matchingSlot.distributions.length === 1 &&
       matchingSlot.distributions[0].userId === userId);
  };

  const handleOpenReassignDialog = (userId) => {
    const slot = {
      day: dayjs(block.start).day(),
      startTime: dayjs(block.start).format('HH:mm'),
      endTime: dayjs(block.end).format('HH:mm'),
      calendrier: block.calendrier,
      source,
    };

    const commercial = commerciaux.find(c => c._id === userId);
    const userLogin = commercial?.login || 'Commercial inconnu';

    utils.openReassignDialog(
      { userId, slot, userLogin, onRefreshData },
      dispatch,
      setDialog
    );
  };

  const getData = useCallback(() => ({
    startTime,
    endTime,
    calendrier,
    distributions,
    source,
  }), [startTime, endTime, calendrier, distributions, source]);

  const validate = useCallback(() => utils.validateBlock({
    startTime,
    endTime,
    block,
    slotInfo,
    existingBlocks,
    calendrier,
    distributions,
    commerciaux,
  }), [
    startTime,
    endTime,
    block,
    slotInfo,
    existingBlocks,
    calendrier,
    distributions,
    commerciaux,
  ]);

  // Exposer getData et validate via Redux contentProps
  useEffect(() => {
    setDialogContentProp('getData')(getData);
    setDialogContentProp('validate')(validate);
  }, [getData, validate, setDialogContentProp]);

  const availableCalendriers = utils
    .getPossibleCalendriersForSlot({
      block,
      slotInfo,
      existingBlocks,
      startTime,
      endTime,
      calendriers,
    });

  const getCommerciaux = (currentUserId) => utils
    .getPossibleCommerciauxForSlot({
      distributions,
      commerciaux,
      currentUserId,
      block,
      slotInfo,
      existingBlocks,
      calendrier,
      startTime,
      endTime,
    });

  return (
    <Box className={classes.container}>
      <Typography variant="h6" gutterBottom>
        {block ? 'Modifier le créneau' : 'Ajouter un créneau'}
      </Typography>

      <Typography variant="body2" color="textSecondary"
        gutterBottom className={classes.dayLabel}>
        {utils.getDayLabel(block, slotInfo)}
      </Typography>

      <Box className={classes.timeSection}>
        <Typography variant="subtitle2" gutterBottom>
          Horaires du créneau
        </Typography>
        <Box className={classes.timeInputsBox}>
          <TimeSelector
            label="Heure de début"
            value={startTime}
            onChange={setStartTime}
            min={LEAD_PLANNING_HOURS.START}
            max={LEAD_PLANNING_HOURS.END}
          />
          <Typography>→</Typography>
          <TimeSelector
            label="Heure de fin"
            value={endTime}
            onChange={setEndTime}
            min={LEAD_PLANNING_HOURS.START}
            max={LEAD_PLANNING_HOURS.END}
          />
        </Box>
      </Box>

      <CalendrierSelector
        calendrier={calendrier}
        setCalendrier={setCalendrier}
        calendriers={availableCalendriers}
      />

      <Divider sx={{ my: 2 }} />

      <Box className={classes.distributionSection}>
        <Box className={classes.distributionHeader}>
          <Typography variant="subtitle2">
            Attribution des commerciaux
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddDistribution}
            size="small"
            variant="outlined"
          >
            Ajouter un commercial
          </Button>
        </Box>

        {distributions.length === 0 ? (
          <Alert severity="info">
            Aucun commercial assigné. Cliquez sur
            "Ajouter un commercial" pour commencer.
          </Alert>
        ) : (
          <Box>
            {distributions.map((dist, index) => (
              <Paper
                key={`dist-${dist.userId || index}`}
                className={classes.distributionItem}
                variant="outlined"
              >
                <Box className={classes.distributionInputs}>
                  <FormControl fullWidth>
                    <InputLabel>Commercial</InputLabel>
                    <Select
                      value={dist.userId}
                      onChange={(e) =>
                        handleDistributionChange(
                          index,
                          'userId',
                          e.target.value
                        )
                      }
                      label="Commercial"
                    >
                      {getCommerciaux(dist.userId).map(commercial => (
                        <MenuItem key={commercial._id} value={commercial._id}>
                          {commercial.login}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Pourcentage (%)"
                    type="number"
                    value={dist.percentage}
                    onChange={(e) =>
                      handleDistributionChange(
                        index,
                        'percentage',
                        e.target.value
                      )
                    }
                    inputProps={{ min: 0, max: 100 }}
                    className={classes.percentageField}
                  />

                  <IconButton
                    onClick={() => handleOpenReassignDialog(dist.userId)}
                    color="warning"
                    title={'Réattribuer les demandes de ce commercial'}
                    disabled={isUserOnlyInSavedSlot(dist.userId)}
                  >
                    <AssignmentIndIcon />
                  </IconButton>

                  <IconButton
                    onClick={() => handleRemoveDistribution(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}

            <Box className={classes.totalBox}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color={utils.getTotalPercentage(distributions) === 100
                  ? 'success.main'
                  : 'error.main'}
              >
                Total: {utils.getTotalPercentage(distributions)}%
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(GestionLeadsConfigDialog, styles)
);
